import { registerLocaleData } from "@angular/common";
import { loadTranslations } from "@angular/localize";
import { setOptions, locale } from "@mobiscroll/angular";
import moment from "moment-timezone";

import { lookupLocale } from "./locale-lookup";
import {
	ANGULAR_LOCALES,
	BUILTIN_LOCALES,
	MOBISCROLL_LOCALES,
	MOMENT_LOCALES,
} from "./locale-mappings";
import { CustomLangs } from "./mobiscroll-locales";

import type { LanguagePack } from "./language-pack";

/**
 * Returns the language code name from the browser, e.g. "fr"
 */
function getBrowserLang(): string {
	if (typeof window === "undefined" || typeof window.navigator === "undefined") {
		return "";
	}

	return getBrowserCultureLang().split("-")[0];
}

/**
 * Returns the culture language code name from the browser, e.g. "fr-FR"
 */
function getBrowserCultureLang(): string {
	if (typeof window === "undefined" || typeof window.navigator === "undefined") {
		return "";
	}

	let browserCultureLang = window.navigator.languages ? window.navigator.languages[0] : null;
	browserCultureLang =
		browserCultureLang ||
		window.navigator.language ||
		(window.navigator as any).browserLanguage ||
		(window.navigator as any).userLanguage;

	return browserCultureLang?.replace(/_/g, "-") ?? "de";
}

/**
 * Fetch the language pack from the server and apply it to angular
 * @param culture Two letter language code of the locale to use
 * @param translationsPath Url path which points to the location where the translation json's are located
 */
export async function loadLanguage(culture: string, translationsPath: string): Promise<void> {
	if (!culture) {
		culture = getBrowserLang();
	}

	await Promise.all([_loadAngularLocale(culture), _loadMomentLocale(culture)]);

	// Also set mobiscroll locale for calendar
	const mobiscrollLocale = lookupLocale(culture, MOBISCROLL_LOCALES);
	const customLang = CustomLangs[mobiscrollLocale];
	setOptions({
		locale: customLang ? customLang : (locale[mobiscrollLocale] ?? locale.de),
	});

	const builtinLocale = lookupLocale(culture, BUILTIN_LOCALES);
	const res = await _fetch(`${translationsPath}/${builtinLocale}.json`);
	if (!res) {
		$localize.locale = culture;
		return;
	}

	const pack: LanguagePack = res as LanguagePack;
	// Set translations to be used by the application
	loadTranslations(pack.translations);
	// Set the LOCALE_ID injection token -> This works by design for now and might break!
	$localize.locale = pack.locale;
}

async function _loadAngularLocale(culture: string): Promise<void> {
	const locale = lookupLocale(culture, ANGULAR_LOCALES);

	// WORKAROUND: Set explicit node_modules path since webpack resolution for certain packages is buggy
	const angularLocale = await import(
		/* webpackInclude: /(de|en|it|fr|hu|cs|sr-Latn|bs-Latn|sk|bg|ro)\.mjs$/ */ `node_modules/@angular/common/locales/${locale}`
	);
	registerLocaleData(angularLocale.default);
}

async function _loadMomentLocale(culture: string): Promise<void> {
	const momentLocale = lookupLocale(culture, MOMENT_LOCALES);
	await import(
		/* webpackInclude: /(de|en-gb|it|fr|hu|cs|sr|bs|sk|bg|ro)\.js$/ */ `moment/locale/${momentLocale}`
	);
	moment.locale(momentLocale);
}

function _fetch(url: string): Promise<unknown> {
	return new Promise((resolve) => {
		fetch(url, {
			headers: {
				Accept: "application/json",
			},
		})
			.then((response) => {
				if (!response.ok) {
					resolve(void 0);
					return;
				}

				response.text().then((data) => {
					if (!data || data === "") {
						resolve(void 0);
						return;
					}

					resolve(JSON.parse(data));
				});
			})
			.catch(() => {
				resolve(void 0);
			});
	});
}
